import { useEffect, useState } from 'react';

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

const GTM_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';
const GTM_CONTAINER_URL = 'https://www.googletagmanager.com/gtm.js';
const GTM_SCRIPT_PARAM_NAME = 'id';
const GTM_ID_PLACEHOLDER = '{GTM_ID}';

function resolveScriptUrl(id: string, src?: string, type?: 'GTM' | 'GA4'): string {
  if (src && src.trim()) {
    return src.replace(new RegExp(GTM_ID_PLACEHOLDER.replace(/[{}]/g, '\\$&'), 'gi'), id);
  }
  if (type === 'GTM') {
    return `${GTM_CONTAINER_URL}?${GTM_SCRIPT_PARAM_NAME}=${id}`;
  }
  return `${GTM_SCRIPT_URL}?${GTM_SCRIPT_PARAM_NAME}=${id}`;
}

interface GtmConfig {
  id: string;
  src?: string;
  type?: 'GTM' | 'GA4';
}

const useGtag = () => {
  const [isDatalayerDefined, setIsDatalayerDefined] = useState(false);

  function gtag(...args: any[]) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args);
  }

  useEffect(() => {
    if (isDatalayerDefined) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };
    window.gtag('js', new Date());
    setIsDatalayerDefined(true);
  }, [isDatalayerDefined]);

  const addGtmScript = (config: GtmConfig): void => {
    const resolvedUrl = resolveScriptUrl(config.id, config.src, config.type);

    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
    }
    const script = document.createElement('script');
    script.src = resolvedUrl;
    script.async = true;
    script.onload = () => {
      if (typeof window.gtag === 'function') {
        window.gtag('config', config.id);
      }
    };
    script.onerror = () => {
      console.warn(`GA/GTM script failed to load: ${config.id}`);
    };
    document.head.insertBefore(script, document.head.childNodes[0]);
  };

  return { addGtmScript, gtag };
};

export default useGtag;
